import { useEffect, useContext } from "react";
import { Group, Button, Text, useMantineTheme } from "@mantine/core";
import { useForm, yupResolver } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import {
  CreatePackageInput,
  GetPackageQuery,
  useCreatePackageMutation,
  useGetLocationsLazyQuery,
  useGetPoliciesQuery,
  useUpdatePackageMutation,
} from "src/generated/graphql";
import { array, object, string } from "yup";
import { useModals } from "@mantine/modals";
import { useNavigate, useParams } from "react-router-dom";
import TextInput from "src/components/TextInput/TextInput";
import Textarea from "src/components/Textarea/Textarea";
import MultiSelect from "src/components/MultiSelect";
import FormCard from "src/components/FormCard";
import { CardRadius, HeightCard, MESSAGES, ROUTES } from "src/utils/constants";
import ActionCard from "src/components/ActionCard";
import Switch from "src/components/Switch/Switch";
import { AppContext } from "src/api/AppContext";

const packageInitialValues = {
  name: "",
  description: "",
  additionalPolicies: [],
  locations: undefined,
  isGeneric: true,
};

const packageFormSchema = object().shape({
  name: string()
    .trim("No spaces allowed")
    .strict(true)
    .min(2, "Name should must be least 2 letters")
    .max(30, "Name must not be greater than 30 letters")
    .required("Name is required"),
  additionalPolicies: array(),
  description: string().max(200).notRequired(),
  // .min(1, "at least 1 Additional Policy is required")
  // .required("Additional Policy is required"),
});

type IFormProps = Omit<
  CreatePackageInput,
  "additionalPolicies" | "locations"
> & {
  additionalPolicies: string[];
  locations?: string[];
};

interface IProps {
  packageData?: GetPackageQuery["packageOne"];
  isOrg?: boolean;
  isAllowWrite: boolean;
  refetchData?: () => void;
}

function PackageForm({
  packageData,
  isOrg,
  isAllowWrite,
  refetchData,
}: IProps) {
  const navigate = useNavigate();
  const modals = useModals();
  const theme = useMantineTheme();
  const { currentLocation } = useContext(AppContext);

  const { pkgId } = useParams<{ pkgId: string }>();
  const { data: policiesArr } = useGetPoliciesQuery();
  const [isFormActive, { toggle: toggleFormActive }] = useDisclosure(!!pkgId);

  const [getLocations, { data: locArr }] = useGetLocationsLazyQuery();

  useEffect(() => {
    if (isOrg) {
      getLocations();
    }
  }, [isOrg]);

  useEffect(() => {
    packageData && resetFormState();
  }, [packageData]);

  const resetFormState = () => {
    if (!packageData) return;
    const { additionalPolicies, name, description, locations } = packageData;
    let _obj = {
      name,
      description,
      additionalPolicies: additionalPolicies.map((e) => e.id),
      locations: locations?.map((e) => e.id),
      isOrg: packageData.isOrg || false,
      isGeneric: Boolean(packageData.isGeneric),
    };
    form.setValues(_obj);
    form.resetDirty(_obj);
  };

  const [updatePackage, { loading: updateLoading }] = useUpdatePackageMutation({
    onCompleted: () => {
      refetchData && refetchData();
      toggleFormActive();
      form.resetDirty();
    },
  });

  const [createPackage, { loading: createLoading }] = useCreatePackageMutation({
    onCompleted: (resp) => {
      if (resp.createPackage) {
        modals.closeAll();
        navigate(
          `${isOrg ? ROUTES.ORG_PACKAGE : ROUTES.PACKAGE}/${
            resp.createPackage.id
          }`
        );
      } else {
        window.location.reload();
      }
    },
  });

  const form = useForm<IFormProps>({
    initialValues: {
      ...packageInitialValues,
      isGeneric: Boolean(packageData?.isGeneric),
      isOrg: !!(isOrg || packageData?.isOrg),
    },
    validate: yupResolver(packageFormSchema),
  });
  const handleSubmit = async (values: IFormProps) => {
    let { locations, isOrg } = values;
    if (!isOrg && !locations?.length) {
      locations = [currentLocation?.id!];
    }
    let _obj = {
      ...values,
      additionalPolicies: values.additionalPolicies?.map((e) => ({
        id: e,
      })),
      locations: (locations || [])?.map((e) => ({ id: e })),
      // isGeneric: Boolean(isOrg),
      // isOrg: isOrg || false,
    };
    if (pkgId) {
      updatePackage({
        variables: {
          data: _obj,
          id: pkgId,
        },
      });
    } else {
      createPackage({
        variables: {
          data: {
            ..._obj,
            // for creation if isOrg is passed then it will be true else false
            isOrg: isOrg || false,
          },
        },
      });
    }
  };

  const handleEdit = () => {
    if (!isOrg) {
      if (form.values.isOrg) {
        modals.openModal({
          closeOnClickOutside: true,
          title: "Error!",
          children: (
            <Text size="sm">
              This is an org-level package. You do not have permission to edit
              this package.
            </Text>
          ),
        });
        return;
      }
    }
    toggleFormActive();
  };

  const handleClick = () => {
    resetFormState();
    toggleFormActive();
  };

  const isFormUpdated = form.isDirty() && form.isTouched();

  return (
    <FormCard
      title={"Basic Info"}
      loading={updateLoading}
      bannerContent={isFormUpdated ? MESSAGES.UNSAVED_CHANGES : ""}
      onCancel={handleClick}
      onEdit={isAllowWrite ? handleEdit : undefined}
      onSave={form.onSubmit((values) => handleSubmit(values))}
      isFormActive={isFormActive}
      hideCard={!pkgId}
    >
      <TextInput
        withAsterisk
        label="Name"
        disabled={isFormActive}
        {...form.getInputProps("name")}
      />

      <Textarea
        label="Description"
        disabled={isFormActive}
        {...form.getInputProps("description")}
      />

      <MultiSelect
        //withAsterisk
        data={
          policiesArr?.policyMany?.map((val) => ({
            value: val.id,
            label: val.name,
          })) || []
        }
        disabled={isFormActive}
        label="Additional Policies"
        {...form.getInputProps("additionalPolicies")}
        styles={{
          input: {
            borderRadius: "5px !important",
          },
        }}
      />

      {isOrg && (
        <ActionCard
          title={"Apply To All Locations"}
          mt={"sm"}
          height={HeightCard.sm}
          radius={CardRadius.sm}
          size="md"
          rightIconButton={
            <Switch
              disabled={isFormActive}
              checked={form.getInputProps("isGeneric")?.value}
              {...form.getInputProps("isGeneric")}
              hasBackground={true}
            />
          }
          hideChild={form.getInputProps("isGeneric")?.value}
          headerOnly
        >
          {!form.getInputProps("isGeneric")?.value && (
            <>
              <Text size={13} ml={7} color={theme.colors.secondaryTextColor[6]}>
                Select the locations to which this agreement should be
                available.
              </Text>
              <MultiSelect
                data={
                  locArr?.locationMany?.map((val) => ({
                    value: val.id,
                    label: val.name,
                  })) || []
                }
                disabled={isFormActive}
                label="Locations"
                {...form.getInputProps("locations")}
                value={form.values.locations}
                styles={{
                  input: {
                    borderRadius: "5px !important",
                  },
                }}
              />
            </>
          )}
        </ActionCard>
      )}

      {!pkgId && (
        <Group position="apart" mt="md" style={{ marginTop: 30 }}>
          <Button
            radius="md"
            variant="outline"
            onClick={() => modals.closeAll()}
            style={{
              flex: 1,
              color: theme.colors.secondaryTextColor[6],
            }}
            sx={{
              backgroundColor: theme.colors.secondary[6],
              "&:hover": {
                background: theme.colors.secondary[5],
              },
            }}
          >
            Cancel
          </Button>
          <Button
            radius="md"
            type="submit"
            disabled={createLoading || updateLoading}
            style={{
              flex: 1,
            }}
            sx={{
              color: theme.colors.primaryTextColor[6],
              "&:hover": {
                backgroundColor: theme.colors.primary[4],
              },
            }}
            loading={createLoading || updateLoading}
          >
            Create
          </Button>
        </Group>
      )}
    </FormCard>
  );
}

export default PackageForm;
